type Resolve<T> = (value: T) => void;
type Reject = (error?: unknown) => void;
type Fetcher<T> = (resolve: Resolve<T>, reject: Reject) => void;
type Unsubscribe = () => void;
type Subscriber<T> = {
  id: symbol;
  onSuccess: Resolve<T>;
  onError?: Reject;
};
type CacheEntry<T> = {
  value: T;
  expiresAt?: number;
};
const DEFAULT_CACHE_TTL_MS = 20 * 1000;

// Shared helper for read-heavy UI fragments that may mount more than once before the
// first request finishes, such as Quick Lists case-profile preview cards.
//
// Usage:
// 1. Build a stable key from the request inputs.
// 2. Call run(key, fetcher, onSuccess, onError) for normal cached/coalesced behavior.
// 3. Call refresh(key, ...) when the data must be reloaded and the cached value should be bypassed.
// 4. Call invalidate(key) after a mutation if the next read should fetch fresh data.
// 5. The default cache window is 1 minute; pass ttlMs only when a caller needs
//    a different lifetime.
//
// The first caller starts the request. Later callers with the same key subscribe to that
// in-flight request and receive the same result instead of issuing duplicate network calls.
// Successful responses are cached by key until invalidated, refreshed, or expired.

interface RequestCoalescerOptions {
  ttlMs?: number;
}

export interface RequestCoalescer<T, K = string> {
  run: (key: K, fetcher: Fetcher<T>, onSuccess: Resolve<T>, onError?: Reject) => Unsubscribe;
  refresh: (key: K, fetcher: Fetcher<T>, onSuccess: Resolve<T>, onError?: Reject) => Unsubscribe;
  invalidate: (key: K) => void;
  clear: () => void;
  hasCachedValue: (key: K) => boolean;
}

export const createRequestCoalescer = <T, K = string>(options: RequestCoalescerOptions = {}): RequestCoalescer<T, K> => {
  const cache = new Map<K, CacheEntry<T>>();
  const subscribers = new Map<K, Subscriber<T>[]>();
  const ttlMs = typeof options.ttlMs === "number"
    ? options.ttlMs
    : DEFAULT_CACHE_TTL_MS;
  const hasTtl = ttlMs > 0;

  const getCacheEntry = (key: K) => {
    const entry = cache.get(key);

    if (!entry) {
      return null;
    }

    if (hasTtl && entry.expiresAt <= Date.now()) {
      cache.delete(key);
      return null;
    }

    return entry;
  };

  const notifySuccess = (key: K, value: T) => {
    cache.set(key, {
      value,
      expiresAt: hasTtl ? Date.now() + ttlMs : undefined,
    });

    const currentSubscribers = subscribers.get(key) ?? [];
    subscribers.delete(key);
    currentSubscribers.forEach((subscriber) => subscriber.onSuccess(value));
  };

  const notifyError = (key: K, error?: unknown) => {
    const currentSubscribers = subscribers.get(key) ?? [];
    subscribers.delete(key);
    currentSubscribers.forEach((subscriber) => subscriber.onError?.(error));
  };

  const removeSubscriber = (key: K, subscriberId: symbol) => {
    const currentSubscribers = subscribers.get(key);

    if (!currentSubscribers) {
      return;
    }

    const remainingSubscribers = currentSubscribers.filter((subscriber) => subscriber.id !== subscriberId);

    if (remainingSubscribers.length === 0) {
      subscribers.delete(key);
      return;
    }

    subscribers.set(key, remainingSubscribers);
  };

  const run = (key: K, fetcher: Fetcher<T>, onSuccess: Resolve<T>, onError?: Reject) => {
    const cachedEntry = getCacheEntry(key);

    if (cachedEntry) {
      onSuccess(cachedEntry.value);
      return () => {};
    }

    const subscriberId = Symbol(String(key));
    const subscriber = { id: subscriberId, onSuccess, onError };

    if (subscribers.has(key)) {
      subscribers.get(key).push(subscriber);
      return () => removeSubscriber(key, subscriberId);
    }

    subscribers.set(key, [subscriber]);

    try {
      fetcher(
        (value) => notifySuccess(key, value),
        (error) => notifyError(key, error),
      );
    } catch (error) {
      notifyError(key, error);
    }

    return () => removeSubscriber(key, subscriberId);
  };

  const invalidate = (key: K) => {
    cache.delete(key);
  };

  const refresh = (key: K, fetcher: Fetcher<T>, onSuccess: Resolve<T>, onError?: Reject) => {
    invalidate(key);
    return run(key, fetcher, onSuccess, onError);
  };

  const clear = () => {
    cache.clear();
    subscribers.clear();
  };

  return {
    run,
    refresh,
    invalidate,
    clear,
    hasCachedValue: (key: K) => Boolean(getCacheEntry(key)),
  };
};